import { CHRONICLE_TYPE_LABELS, type ChronicleWeek } from '@brimstone/shared';
import type { ReactNode } from 'react';
import { useSearchParams } from 'react-router';
import { ChronicleFeed } from '../../components/ChronicleFeed';
import { PhotoGrid } from '../../components/Photos';
import { Chip, EmptyState, PageLayout, Panel, SecondaryButton } from '../../components/ui';
import { useChronicle, type ChronicleFilters } from '../../lib/chronicle';
import { usePhotos } from '../../lib/photos';

const list = (v: string | null) => (v ? v.split(',').filter(Boolean) : []);

/** "18 Nov – 25 Nov" for a reset week. */
function weekLabel(w: ChronicleWeek): string {
  const d = (iso: string) =>
    new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
  return `${d(w.startAt)} – ${d(w.endAt)}`;
}

function FilterRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-label uppercase tracking-label text-muted">{label}</span>
      {/* One scrolling row on a phone, wrapped on wider screens: the page never scrolls sideways. */}
      <div className="-mx-4 flex gap-2 overflow-x-auto px-4 sm:mx-0 sm:flex-wrap sm:px-0">
        {children}
      </div>
    </div>
  );
}

/** The Photos view (M12): every screenshot by day, filtered by character and week. */
function PhotosView({
  characterIds,
  week,
  toggle,
  clear,
}: {
  characterIds: string[];
  week: string | null;
  toggle: (id: string) => void;
  clear: () => void;
}) {
  const q = usePhotos({ characterIds, week });
  const first = q.data?.pages[0];
  const photos = q.data?.pages.flatMap((p) => p.photos) ?? [];
  return (
    <>
      {first && first.characters.length > 0 && (
        <div className="border-b border-line px-4 py-3">
          <FilterRow label="Character">
            <Chip pressed={characterIds.length === 0} onClick={clear}>
              All
            </Chip>
            {first.characters.map((c) => (
              <Chip
                key={c.id ?? 'unknown'}
                pressed={characterIds.includes(c.id ?? 'unknown')}
                count={c.count}
                onClick={() => toggle(c.id ?? 'unknown')}
              >
                {c.name ?? 'Unknown'}
              </Chip>
            ))}
          </FilterRow>
        </div>
      )}
      {!q.data ? (
        <EmptyState>{q.isError ? 'Couldn’t load the photos.' : 'Loading…'}</EmptyState>
      ) : photos.length === 0 ? (
        <EmptyState>
          {first?.total
            ? 'No photos match these filters.'
            : 'No photos yet — Memento’s screenshots appear here once the watcher uploads them.'}
        </EmptyState>
      ) : (
        <>
          <PhotoGrid photos={photos} label="Photos" byDay />
          {q.hasNextPage && (
            <div className="border-t border-line px-4 py-3">
              <SecondaryButton
                type="button"
                disabled={q.isFetchingNextPage}
                onClick={() => void q.fetchNextPage()}
              >
                {q.isFetchingNextPage ? 'Loading…' : 'Older photos'}
              </SecondaryButton>
            </div>
          )}
        </>
      )}
    </>
  );
}

/**
 * /chronicle (SPEC §6.6): the journal, filterable by character, type and week; `view=photos`
 * shows the screenshots instead (M12).
 */
export function ChroniclePage() {
  const [params, setParams] = useSearchParams();
  const view = params.get('view') === 'photos' ? 'photos' : 'entries';
  const filters: ChronicleFilters = {
    characterIds: list(params.get('character')),
    types: list(params.get('type')),
    week: params.get('week'),
  };
  // In the Photos view the feed only gives the weeks, which ignore the filters.
  const feed = useChronicle(
    view === 'photos' ? { characterIds: [], types: [], week: null } : filters,
  );
  const first = feed.data?.pages[0];
  const entries = feed.data?.pages.flatMap((p) => p.entries) ?? [];

  const set = (key: 'character' | 'type' | 'week' | 'view', values: string[]) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (values.length) next.set(key, values.join(','));
        else next.delete(key);
        return next;
      },
      { replace: true },
    );
  const toggle = (key: 'character' | 'type', current: string[], value: string) =>
    set(key, current.includes(value) ? current.filter((v) => v !== value) : [...current, value]);

  const total = first?.facets.types.reduce((s, t) => s + t.count, 0);

  return (
    <PageLayout
      main={
        <Panel
          title="Chronicle"
          meta={view === 'entries' && total !== undefined ? `${total} ENTRIES` : undefined}
        >
          <div className="flex flex-col gap-3 border-b border-line px-4 py-3">
            <div className="flex flex-wrap gap-2" role="group" aria-label="View">
              <Chip pressed={view === 'entries'} onClick={() => set('view', [])}>
                Entries
              </Chip>
              <Chip pressed={view === 'photos'} onClick={() => set('view', ['photos'])}>
                Photos
              </Chip>
            </div>
            <label className="flex flex-wrap items-center gap-3">
              <span className="text-label uppercase tracking-label text-muted">Week</span>
              <select
                value={filters.week ?? ''}
                onChange={(e) => set('week', e.target.value ? [e.target.value] : [])}
                className="min-h-11 border border-line bg-bg px-3 font-mono text-secondary"
              >
                <option value="">All weeks</option>
                {first?.weeks.map((w) => (
                  <option key={w.id} value={w.id}>
                    {weekLabel(w)} · {w.count}
                  </option>
                ))}
              </select>
            </label>
            {view === 'entries' && first && first.facets.characters.length > 0 && (
              <FilterRow label="Character">
                <Chip
                  pressed={filters.characterIds.length === 0}
                  onClick={() => set('character', [])}
                >
                  All
                </Chip>
                {first.facets.characters.map((c) => (
                  <Chip
                    key={c.id}
                    pressed={filters.characterIds.includes(c.id)}
                    count={c.count}
                    onClick={() => toggle('character', filters.characterIds, c.id)}
                  >
                    {c.name}
                  </Chip>
                ))}
              </FilterRow>
            )}
            {view === 'entries' && first && first.facets.types.length > 0 && (
              <FilterRow label="Type">
                <Chip pressed={filters.types.length === 0} onClick={() => set('type', [])}>
                  All
                </Chip>
                {first.facets.types.map((t) => (
                  <Chip
                    key={t.type}
                    pressed={filters.types.includes(t.type)}
                    count={t.count}
                    onClick={() => toggle('type', filters.types, t.type)}
                  >
                    {CHRONICLE_TYPE_LABELS[t.type]}
                  </Chip>
                ))}
              </FilterRow>
            )}
          </div>
          {view === 'photos' ? (
            <PhotosView
              characterIds={filters.characterIds}
              week={filters.week}
              toggle={(id) => toggle('character', filters.characterIds, id)}
              clear={() => set('character', [])}
            />
          ) : !feed.data ? (
            <EmptyState>{feed.isError ? 'Couldn’t load the Chronicle.' : 'Loading…'}</EmptyState>
          ) : entries.length === 0 ? (
            <EmptyState>
              {total
                ? 'Nothing matches these filters.'
                : 'No entries yet — they appear after the next upload.'}
            </EmptyState>
          ) : (
            <>
              <ChronicleFeed entries={entries} />
              {feed.hasNextPage && (
                <div className="border-t border-line px-4 py-3">
                  <SecondaryButton
                    type="button"
                    disabled={feed.isFetchingNextPage}
                    onClick={() => void feed.fetchNextPage()}
                  >
                    {feed.isFetchingNextPage ? 'Loading…' : 'Older entries'}
                  </SecondaryButton>
                </div>
              )}
            </>
          )}
        </Panel>
      }
    />
  );
}
